import React, { useState } from 'react';
import { Users, Award, Smile, Check, Sparkles, Heart } from 'lucide-react';

export default function About() {
  const [showFullStory, setShowFullStory] = useState(true);
  const storyParagraphs = [
    'MyEventMitra was born from a simple yet powerful question: "Why is it so hard to find the right people for our Indian housewarming puja in the US?"',
    'Like many Indian families abroad, we were trying to recreate the warmth, rituals, and essence of our culture in a land far from home. But finding a priest, decorator, or caterer often meant dozens of phone calls, WhatsApp forwards, and uncertain pricing.',
    'So we decided to fix that by building a trusted platform that makes traditions effortless and joyful.'
  ];
  const previewParagraph = storyParagraphs[0];

  const stats = [
    { icon: Users, label: '100+ Vendors', color: 'from-blue-500 to-blue-600' },
    { icon: Award, label: '99% Satisfaction', color: 'from-green-500 to-green-600' },
    { icon: Smile, label: '100+ People Benefited', color: 'from-purple-500 to-purple-600' },
  ];

  const goals = [
    'Authentic Indian vendors at your fingertips.',
    'Transparent pricing and clear quotes.',
    'Community-driven, culture-first platform.',
    'Innovating for Indian families abroad.',
  ];

  const team = [
    {
      img: '/assets/jayanta.jpg',
      name: 'Jayanta Kumar Rout',
      role: 'Founder & Lead Developer',
      gradient: 'from-orange-400 to-pink-500'
    },
    {
      img: '/assets/suresh.jpg',
      name: 'Suresh Kumar Ontimitta',
      role: 'Co-Developer & Strategist',
      gradient: 'from-blue-400 to-indigo-500'
    },
    {
      img: '/assets/people.png',
      name: 'Indian Community',
      role: 'Our Strength',
      gradient: 'from-green-400 to-teal-500'
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-4 -right-4 w-72 h-72 bg-gradient-to-br from-orange-200/30 to-amber-200/30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 -left-8 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col-reverse lg:flex-row items-center justify-between pt-8 pb-12 gap-8">
            <div className="lg:w-1/2 text-center lg:text-left space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full text-orange-800 font-medium text-sm">
                <Sparkles className="w-4 h-4 mr-2" />
                Spreading Joy & Culture
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight">
                We are spreading{' '}
                <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
                  Happiness
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed max-w-lg">
                Whether it's a big event plan or small celebration, we're here to make your special moments unforgettable!
              </p>

              <div className="flex items-center justify-center lg:justify-start space-x-4 pt-4">
                <div className="flex items-center text-sm text-gray-500">
                  <Heart className="w-4 h-4 mr-1 text-red-400" />
                  Made with love for our community
                </div>
              </div>
            </div>
            
            <div className="lg:w-1/2 flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-orange-400 to-amber-400 rounded-full blur-lg opacity-20 animate-pulse"></div>
                <img
                  src="/assets/zoom-bg.png"
                  alt="Happy Indian Family"
                  className="relative w-64 h-64 lg:w-80 lg:h-80 object-cover rounded-full shadow-2xl border-4 border-white"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Our Story Section */}
        <div className="max-w-6xl mx-auto relative">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-8 lg:p-12 transform hover:scale-[1.01] transition-all duration-300">
            <div className="text-center mb-8">
              <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-4">
                Our Story
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-amber-400 mx-auto rounded-full"></div>
            </div>
            
            <div className="prose prose-lg max-w-4xl mx-auto">
              {!showFullStory ? (
                <p className="text-xl leading-relaxed text-gray-700 font-light">{previewParagraph}</p>
              ) : (
                storyParagraphs.map((par, idx) => (
                  <p key={idx} className="text-xl leading-relaxed text-gray-700 font-light mb-6 first:text-2xl first:font-normal">
                    {par}
                  </p>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="max-w-6xl mx-auto mt-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map(({ icon: Icon, label, color }, idx) => (
              <div key={label} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-white to-gray-50 rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:-translate-y-1"></div>
                <div className="relative p-8 text-center">
                  <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${color} p-4 shadow-lg`}>
                    <Icon className="w-full h-full text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">{label}</h3>
                  <div className="mt-2 w-12 h-1 bg-gradient-to-r from-orange-400 to-amber-400 rounded-full mx-auto"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Goals Section */}
        <div className="max-w-7xl mx-auto mt-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                  Our <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">Goals</span>
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-amber-400 rounded-full"></div>
              </div>
              
              <ul className="space-y-6">
                {goals.map((item, idx) => (
                  <li key={item} className="flex items-start group">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-orange-400 to-amber-400 flex items-center justify-center shadow-lg mr-4 group-hover:scale-110 transition-transform duration-200">
                      <Check className="w-5 h-5 text-white font-bold" />
                    </div>
                    <span className="text-xl text-gray-700 leading-relaxed font-medium group-hover:text-gray-900 transition-colors duration-200">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-orange-200 to-amber-200 rounded-3xl blur-lg opacity-30"></div>
              <img
                src="/assets/goal.png"
                alt="Our Goals"
                className="relative w-full h-auto rounded-3xl shadow-2xl border-4 border-white"
              />
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className="max-w-6xl mx-auto mt-24 pb-16">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Meet Our <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">Team</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-amber-400 mx-auto rounded-full"></div>
            <p className="mt-6 text-xl text-gray-600 max-w-2xl mx-auto">
              The passionate individuals behind MyEventMitra's success
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map(({ img, name, role, gradient }, idx) => (
              <div key={name} className="group text-center">
                <div className="relative inline-block mb-6">
                  <div className={`absolute -inset-2 bg-gradient-to-r ${gradient} rounded-full blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300`}></div>
                  <img
                    src={img}
                    alt={name}
                    className="relative w-40 h-40 rounded-full object-cover border-4 border-white shadow-xl group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className={`absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-r ${gradient} rounded-full border-4 border-white shadow-lg`}></div>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors duration-200">
                  {name}
                </h3>
                <p className="text-lg text-gray-600 font-medium">{role}</p>
                
                <div className="mt-4 w-16 h-1 bg-gradient-to-r from-gray-300 to-gray-400 rounded-full mx-auto group-hover:from-orange-400 group-hover:to-amber-400 transition-all duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}